import { cn } from "@/lib/utils";
import { daysBetween, formatShortDate, formatDay } from "@/lib/campaignPlanning";

/**
 * The campaign's runway, from today to the day donations close: time to
 * set up, early donations, reading, and late donations. A thin track sized by days, with each stage labelled underneath, so
 * the admin can see how much setup time is left at a glance.
 */
export default function SetupTimeline({ keyDates, className }) {
  const { today, donations_open_on: open, start_on: start, end_on: end, donations_close_on: close } = keyDates;
  if (!open) return null;

  const rangeStart = today < open ? today : open;
  const total = Math.max(1, daysBetween(rangeStart, close));
  const pct = (iso) => Math.min(100, Math.max(0, (daysBetween(rangeStart, iso) / total) * 100));
  const setupDays = daysBetween(today, open);

  const segments = [
    today < open && { from: today, to: open, label: "Get set up", detail: `${setupDays} day${setupDays === 1 ? "" : "s"} left`, className: "bg-primary/20" },
    { from: open, to: start, label: "Early donations", detail: `From ${formatShortDate(open)}`, className: "bg-accent/30" },
    { from: start, to: end, label: "Reading", detail: `${formatShortDate(start)} – ${formatShortDate(end)}`, className: "bg-primary/70" },
    { from: end, to: close, label: "Late donations", detail: `Until ${formatShortDate(close)}`, className: "bg-secondary" },
  ].filter(Boolean);
  const widths = segments.map((s) => pct(s.to) - pct(s.from));
  const todayInside = today >= open && today <= close;

  return (
    <div className={className}>
      {/* Wide screens: a proportional track */}
      <div className="hidden sm:block">
        <div className="relative flex h-1.5 overflow-hidden rounded-full">
          {segments.map((segment, i) => (
            <div
              key={segment.label}
              className={cn("h-full", segment.className)}
              style={{ width: `${widths[i]}%` }}
              title={`${segment.label}: ${formatDay(segment.from)} – ${formatDay(segment.to)}`}
            />
          ))}
          {todayInside && (
            <div className="absolute inset-y-0 w-0.5 bg-foreground" style={{ left: `${pct(today)}%` }} title="Today" />
          )}
        </div>
        <div className="mt-1.5 flex text-[11px] leading-tight">
          {segments.map((segment, i) => (
            <div key={segment.label} className="min-w-0 pr-2" style={{ width: `${widths[i]}%` }}>
              {widths[i] >= 9 && (
                <>
                  <p className="truncate font-medium text-foreground">{segment.label}</p>
                  <p className="truncate text-muted-foreground">{segment.detail}</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Phones: one line per stage */}
      <dl className="grid gap-y-1 text-xs sm:hidden">
        {segments.map((segment) => (
          <div key={segment.label} className="flex items-center gap-1.5">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", segment.className)} />
            <dt className="font-medium text-foreground">{segment.label}</dt>
            <dd className="text-muted-foreground">{segment.detail}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
